/**
 * em-smart-landing.js — size- and stock-aware landing for the PDP tile
 * selectors and the home SPA seed (Mark, 24 Aug 2026).
 *
 * The static tile maps and seasonal seeds name each set's CANONICAL face
 * (e.g. maternity leggings = 7/8 pockets black). This resolver decides where
 * a click on that set actually LANDS:
 *
 *   in stock at her size  ->  pre-orderable at her size  ->  the canonical.
 *
 * Her size = the in-page size tap (__emUserSizePick), else the sizing
 * wizard's sizePants/sizeTops (sessionStorage convomesh_person; sizeBump is
 * AI-chat-only per Mark), else the store's popularity ladder
 * M > S > L > XL > XS > XXL > XXXL > XXS. Values the ladder doesn't
 * recognise (e.g. numeric "AU 12") are treated as unknown.
 *
 * The set is garment-strict by construction: it is every catalog product
 * sharing the canonical's range key (core name minus length tokens), so
 * leggings never land on shorts. Ties inside a size prefer the canonical;
 * otherwise the CLOSEST-TO-HERO identity (Mark, 24 Aug: pockets state
 * first, then nearest length on the spectrum, then same colour), with
 * popularity-weighted size coverage and stock depth breaking what's left.
 * Pre-order ties take the earliest ship window, then closeness.
 *
 * The whole thing is synchronous against window.__emPackCatalog (inline on
 * every page), so it also runs at pointerdown to keep the prefetch warm for
 * the RIGHT page. Every failure path returns null and the caller keeps the
 * static handle.
 */
(function () {
  'use strict';
  if (window.__emSmartLanding) return;

  var LADDER = ['M', 'S', 'L', 'XL', 'XS', 'XXL', 'XXXL', 'XXS'];
  var ALIAS = { '2XL': 'XXL', '3XL': 'XXXL', '2XS': 'XXS' };

  function catalog() { return window.__emPackCatalog || null; }

  // Range key: core name minus the length tokens — mirrors the tile dedupe
  // in pdp-gallery-block.liquid. Encodes the garment ("maternity leggings"
  // vs "maternity shorts"), which is what keeps the resolver set-strict.
  function cnKey(p) {
    return String(p.coreName || p.title || '').toLowerCase()
      .replace(/3\/4/g, '').replace(/7\/8/g, '').replace(/full length/g, '')
      .replace(/\s+/g, ' ').trim();
  }
  function garmentClass(p) {
    var b = String(p.bucket || '');
    return (b.indexOf('|leggings') >= 0 || b.indexOf('|shorts') >= 0) ? 'bottoms' : 'tops';
  }
  var SPECTRUM = ['shorts', 'long shorts', '3/4', '7/8', 'full length'];
  function lower(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  // Identity view over a catalog product — same normalisation rules as the
  // family configurator (bike shorts = shorts; missing length = full length;
  // missing colour = black).
  function identOf(p) {
    var len;
    if (String(p.bucket || '').indexOf('|shorts') >= 0) {
      var raw = lower(p.length);
      len = raw === 'long shorts' ? 'long shorts' : 'shorts';
    } else {
      len = lower(p.length) || 'full length';
    }
    return {
      length: len,
      feature: lower(p.feature) === 'with pockets' ? 'with pockets' : 'no pockets',
      colour: lower(p.color) || 'black'
    };
  }
  // Closeness to the hero's identity (Mark, 24 Aug): pockets state first,
  // then nearest length, then same colour. Lower tuple = closer. Tops carry
  // no meaningful axes, so everything ties and falls to coverage.
  function closenessTo(hero) {
    var hi = identOf(hero);
    var hLen = SPECTRUM.indexOf(hi.length);
    return function (p) {
      var i = identOf(p);
      var iLen = SPECTRUM.indexOf(i.length);
      var lenDist = (hLen < 0 || iLen < 0) ? 0 : Math.abs(iLen - hLen);
      return [i.feature === hi.feature ? 0 : 1, lenDist, i.colour === hi.colour ? 0 : 1];
    };
  }
  function byCloseness(close) {
    return function (a, b) {
      var ca = close(a), cb = close(b);
      for (var x = 0; x < ca.length; x++) if (ca[x] !== cb[x]) return ca[x] - cb[x];
      return 0;
    };
  }
  function normSize(v) {
    var s = String(v == null ? '' : v).trim().toUpperCase();
    s = ALIAS[s] || s;
    return LADDER.indexOf(s) >= 0 ? s : null;
  }
  function sizeInfo(garment) {
    var p = window.__emUserSizePick;
    var s = p && normSize(p.size);
    if (s) return { size: s, source: 'pick' };
    try {
      var raw = sessionStorage.getItem('convomesh_person');
      if (raw) {
        var per = JSON.parse(raw);
        if (per && per.person) per = per.person;
        var field = garment === 'tops' ? 'sizeTops' : 'sizePants';
        var val = (per && per.sizes && per.sizes[field]) || (per && per[field]);
        s = normSize(val);
        if (s) return { size: s, source: 'wizard' };
      }
    } catch (e) {}
    return { size: null, source: 'popular' };
  }
  function vAt(p, size) {
    var vs = p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (String(vs[i].size || '').toUpperCase() === size) return vs[i];
    }
    return null;
  }
  // preorderConfig is a top-level `let` in packprice's classic script —
  // visible here through the global lexical environment (same pattern as
  // the family configurator); guard for load order anyway.
  function cfgEntry(sku) {
    try {
      var cfg = (typeof preorderConfig !== 'undefined' && Array.isArray(preorderConfig)) ? preorderConfig : null;
      if (!cfg || !sku) return null;
      for (var i = 0; i < cfg.length; i++) {
        var e = cfg[i];
        if (e && e.sku === sku && (e.qty || 0) > 0) return e;
      }
    } catch (e) {}
    return null;
  }
  // Popularity-weighted size coverage: a product stocked across the popular
  // sizes beats one alive in a single niche size.
  function coverage(p) {
    var sc = 0;
    for (var i = 0; i < LADDER.length; i++) {
      var v = vAt(p, LADDER[i]);
      if (v && (v.qty || 0) > 0) sc += (LADDER.length - i);
    }
    return sc;
  }
  function productBy(handle) {
    var cat = catalog();
    if (!cat || !cat.products) return null;
    for (var i = 0; i < cat.products.length; i++) {
      if (cat.products[i].handle === handle) return cat.products[i];
    }
    return null;
  }
  function emit(surface, from, to, stage, size, source) {
    try {
      if (typeof window.__scvEmit === 'function') {
        window.__scvEmit('fc_smart_landing', {
          source_surface: 'smart_landing', surface: surface,
          from_handle: from, to_handle: to, stage: stage,
          size: size || '', size_source: source
        });
      }
    } catch (e) {}
  }

  function resolve(canonicalHandle, surface, quiet) {
    try {
      var canonical = productBy(canonicalHandle);
      if (!canonical) return null;
      var key = cnKey(canonical);
      var set = catalog().products.filter(function (p) { return cnKey(p) === key; });
      if (!set.length) return null;
      var info = sizeInfo(garmentClass(canonical));
      var sizes = info.size ? [info.size] : LADDER;
      var j, s, k;
      // 1. in stock — first size in the list with a live member wins the size
      for (j = 0; j < sizes.length; j++) {
        s = sizes[j];
        var inStock = set.filter(function (p) {
          var v = vAt(p, s);
          return v && (v.qty || 0) > 0;
        });
        if (!inStock.length) continue;
        var pick = null;
        for (k = 0; k < inStock.length; k++) {
          if (inStock[k].handle === canonicalHandle) { pick = inStock[k]; break; }
        }
        if (!pick) {
          var cmpClose = byCloseness(closenessTo(canonical));
          inStock.sort(function (a, b) {
            return cmpClose(a, b)
              || coverage(b) - coverage(a)
              || ((vAt(b, s) || {}).qty || 0) - ((vAt(a, s) || {}).qty || 0);
          });
          pick = inStock[0];
        }
        if (!quiet && pick.handle !== canonicalHandle) emit(surface, canonicalHandle, pick.handle, 'stock', s, info.source);
        return pick.handle;
      }
      // 2. pre-order — purchasable + in preorder-config; earliest ship first
      for (j = 0; j < sizes.length; j++) {
        s = sizes[j];
        var pre = [];
        for (k = 0; k < set.length; k++) {
          var v2 = vAt(set[k], s);
          if (!v2 || !v2.avail) continue;
          var e = cfgEntry(v2.sku);
          if (e) pre.push({ p: set[k], eta: e._eta || '9999' });
        }
        if (!pre.length) continue;
        var cmpPre = byCloseness(closenessTo(canonical));
        pre.sort(function (a, b) {
          if (a.eta !== b.eta) return a.eta < b.eta ? -1 : 1;
          return cmpPre(a.p, b.p);
        });
        var ph = pre[0].p.handle;
        if (!quiet && ph !== canonicalHandle) emit(surface, canonicalHandle, ph, 'preorder', s, info.source);
        return ph;
      }
      // 3. nothing buyable anywhere in the set — the canonical lands and the
      // PDP's own rescue UI (State C card / Ring 2) takes it from there.
      return canonicalHandle;
    } catch (e) { return null; }
  }

  // Tile-highlight helper: a smart landing usually isn't the tile's own
  // representative handle — same range still counts as "this tile".
  function sameRange(h1, h2) {
    try {
      if (!h1 || !h2) return false;
      if (h1 === h2) return true;
      var a = productBy(h1), b = productBy(h2);
      return !!(a && b && cnKey(a) === cnKey(b));
    } catch (e) { return false; }
  }

  window.__emSmartLanding = { resolve: resolve, sameRange: sameRange, sizeInfo: sizeInfo };

  // Home initial load: the server seeded the page blind to her size (the
  // Liquid layer only guarantees the seed has SOME stock). When her size is
  // KNOWN and the seed isn't what she can actually buy in it, correct once,
  // silently. Unknown sizes never see a load-time swap. Delayed a beat so
  // the UTM tab-replay swap (also DCL) settles first — after it, resolving
  // the landed handle is a no-op.
  function correctHomeSeed() {
    try {
      if (!window.v2HomeMode) return;
      var cur = window.pdpMeta && window.pdpMeta.handle;
      if (!cur) return;
      var canonical = productBy(cur);
      if (!canonical) return;
      if (!sizeInfo(garmentClass(canonical)).size) return;
      var to = resolve(cur, 'home_seed');
      if (to && to !== cur && typeof window._v2SwapToHandle === 'function') {
        window._v2SwapToHandle(to, { preserveScroll: true });
      }
    } catch (e) {}
  }
  function scheduleCorrection() { setTimeout(correctHomeSeed, 800); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleCorrection, { once: true });
  else scheduleCorrection();
})();
